import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import { recordStatisticsEvent } from '../../../../services/statistics-service';
import WireCaption from './wire-caption';
import WireTitle from './wire-title';
import WireSummary from '../../../wire/wire-summary';

const HFlex = styled.div`
  display: flex;
`;

const Container = styled.section`
  ${props => (props.theme.isMobile ? `
    margin: 0 -20px;
    padding-left: 20px;
    padding-right: 20px;

    &:first-child {
      margin-top: -8px;
    }

    &:not(:first-child) {
      padding-top: 16px;
    }
  ` : `
    margin: 0px -26px;
    padding-left: 26px;
    padding-right: 26px;
    padding-top: 11px;
    padding-bottom: 4px;
  `)};

  .wire__summary {
    margin: 4px 0 24px 0;
    ${props => props.theme.isMobile && `
      margin-top: 14px;
    `};

    > .clampped-summary {
      line-height: 130%;

      .show-more {
        font-weight: ${props => props.theme.fontWeightHeavy};
      }
    }
  }
`;

const HFlexContent = styled.div`
  width: 74%;
  margin-right: 16px;
`;

const HFlexImage = styled.img`
  border-radius: 2px;

  ${props => props.theme.isXLarge && `
    width: 180px;
    height: 100px;
  `};

  ${props => props.theme.isLarge && `
    width: 160px;
    height: 90px;
  `};

  ${props => props.theme.isMobileOrTablet && `
    width: 107px;
    height: 60px;
  `};

  ${props => props.theme.isMobile && `
    width: 100px;
    height: 56px;

    &.sponsored {
      margin-top: 22px;
    }
  `};
`;

const Link = styled.a`
  color: ${props => props.theme.bodyColor};

  &:hover {
    text-decoration: none !important;
    color: ${props => props.theme.bodyColor};
  }
`;

export default function Wire({ wire, profile }) {
  const { isMobile } = React.useContext(ThemeContext);

  const {
    slug,
    title,
    summary,
    sharingImageUrl,
    isSponsored,
  } = wire;

  const handleClick = eventName => {
    recordStatisticsEvent({
      event: eventName,
      statisticable: 'profile',
      id: profile.id,
      clicked_url: `/wires/${slug}`,
      page: window.location.pathname,
    });

    return false;
  };

  return (
    <Container>
      {!!sharingImageUrl && (
      <HFlex>
        <HFlexContent>
          <WireCaption wire={wire} />
          <Link href={`/wires/${slug}`} onClick={() => handleClick('contributor_profile_latest_wire_view')}>
            <WireTitle wire={wire} />
            {!isMobile && (
              <WireSummary
                allowFullSummary={false}
                summary={summary}
                showMoreLink={false}
                url={`/wires/${slug}`}
              />
            )}
          </Link>
        </HFlexContent>
        <Link href={`/wires/${slug}`} onClick={() => handleClick('contributor_profile_latest_wire_view')}>
          <HFlexImage
            className={isSponsored ? 'sponsored' : ''}
            src={sharingImageUrl}
            alt={title}
            title={title}
          />
        </Link>
      </HFlex>
      )}
      {!sharingImageUrl && (
        <React.Fragment>
          <WireCaption wire={wire} />
          <Link href={`/wires/${slug}`} onClick={() => handleClick('contributor_profile_latest_wire_view')}>
            <WireTitle wire={wire} />
            {!isMobile && (
              <WireSummary
                allowFullSummary={false}
                summary={summary}
                showMoreLink={false}
                url={`/wires/${slug}`}
              />
            )}
          </Link>
        </React.Fragment>
      )}
      {!!isMobile && (
        <Link href={`/wires/${slug}`} onClick={() => handleClick('contributor_profile_latest_wire_view')}>
          <WireSummary
            allowFullSummary={false}
            summary={summary}
            showMoreLink={false}
            lines={3}
            url={`/wires/${slug}`}
          />
        </Link>
      )}
    </Container>
  );
}

Wire.propTypes = {
  wire: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    firstPublishedAt: PropTypes.string.isRequired,
    publishedAt: PropTypes.string.isRequired,
    isSponsored: PropTypes.bool.isRequired,
    userId: PropTypes.number.isRequired,
    editorsPicked: PropTypes.bool.isRequired,
    sharingImageUrl: PropTypes.string,
    ownedByCurrentUser: PropTypes.bool.isRequired,
    featuredCount: PropTypes.number.isRequired,
    category: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    series: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  }).isRequired,
  profile: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
};
